import http from '@/http';
import router from '@/router';

import {entityTypePluralize} from './contentHelpers';
import {
  CLEAR_RELATED,
  CONTENT_RECEIVED, RELATED_RECEIVED, RELATED_UPDATE_LIMIT, RELATED_UPDATE_OFFSET,
  SET_IS_LOADING_RELATED, CLEAR_RELATED_EXCEPT_TOTAL
} from './contentTypes';
import { getRoute } from "@/router/getRoute";
import { getRelatedAsMatrix, getUrlPathForRelated } from './helper';
import { uniqueRelatedMedia } from '@/helpers/relatedMediaOrdering'

const state = getDefaultState();

const getters = {
  relatedAsMatrix(state) {
    return getRelatedAsMatrix(state.items)
  },
  relatedAsMatrixMobile(state) {
    return getRelatedAsMatrix(state.items, true)
  },
  relatedPage(state) {
    return Math.ceil(state.offset + 1)
  },
  hasAllRelated(state) {
    if (!state.pageCount && !state.pageNum) {
      return false
    }
    return (state.pageCount === state.pageNum)
      || (state.filteredTotal === state.items.length)
  }
}

const actions = {
  async getRelated({ commit, state, rootState }, filter = "") {
    try {
      if (rootState.content.isLoaded && !state.isLoaded) {
        if (rootState.user.accessPrivileges.indexOf('VIEW_RELATED_ITEMS') >= 0 ||
            rootState.user.accessPrivileges.indexOf('VIEW_ATTACHMENTS') >= 0) {
          commit(SET_IS_LOADING_RELATED, true)
          const entityTypePlural = entityTypePluralize(rootState.content.type);
          const entityId = rootState.content.id;

          const params = {
            limit: state.limit,
            offset: state.offset * state.limit
          };

          const queryParams = !!filter
            ? `includeAllMedia=true&filter=${filter}`
            : "includeAllMedia=true"

          const { headers, data } = await http.get(`${entityTypePlural}/${entityId}/related?${queryParams}`, {params});

          // make this a helper function that can be reused & tested
          const items = data.map(getUrlPathForRelated);

          const related = (state.offset === 0)
            ? items
            : [ ...state.items, ...items ];
          commit(RELATED_RECEIVED, { related, meta: headers });
        } else {
          const currentRoute = getRoute()
          const contentPath = currentRoute.path.substring(0, currentRoute.path.indexOf('related'));
          router.replace({path: `${contentPath}details`});
        }
      }
    } catch (err) {
      commit(RELATED_RECEIVED, {related: []});
    } finally {
      commit('SET_IS_LOADING_RELATED', false);
    }
  },
  setRelatedPage({commit, dispatch}, page) {
    commit(RELATED_UPDATE_OFFSET, {
      offset: Math.max(page, 0)
    });
    dispatch('getRelated');
  },
  setRelatedResultsPerPage({commit, dispatch}, limit) {
    commit(RELATED_UPDATE_LIMIT, {limit});
    dispatch('getRelated');
  },
  async getMoreRelated({ commit, dispatch, getters }) {
    if (getters.hasAllRelated) return

    const offset = Math.max(getters.relatedPage, 0)
    commit(RELATED_UPDATE_OFFSET, offset)
    await dispatch('getRelated')
  },
  resetRelatedOffset({ commit }) {
    commit(RELATED_UPDATE_OFFSET, 0)
  },
  clearRelated({ commit }, includeTotal = true) {
    if (!includeTotal) {
      commit(CLEAR_RELATED_EXCEPT_TOTAL)
    } else {
      commit(CLEAR_RELATED)
    }
  }
};

const mutations = {
  [RELATED_RECEIVED](state, {related, meta}) {
    state.items = uniqueRelatedMedia(related);
    state.total = meta['x-total'] ? Number(meta['x-total']) : 0;
    state.filteredTotal = meta['x-count'] ? Number(meta['x-count']) : 0;
    state.pageCount = meta['x-page-count']
      ? Number(meta['x-page-count'])
      : 0;
    state.pageNum = meta['x-page-num']
      ? Number(meta['x-page-num'])
      : 0;
  },
  [CONTENT_RECEIVED](state, {content}) {
    state.items = [];
    state.offset = 0;
    state.limit = 20;
    state.total = content.relatedCount || 0;
    state.pageCount = 0;
    state.pageNum = 0;
    state.filteredTotal = content.relatedCount || 0;
  },
  [RELATED_UPDATE_OFFSET](state, offset) {
    state.offset = offset;
  },
  [RELATED_UPDATE_LIMIT](state, {limit}) {
    state.limit = limit;
    state.offset = 0;
  },
  [CLEAR_RELATED] (state) {
    Object.assign(state, getDefaultState())
  },
  [CLEAR_RELATED_EXCEPT_TOTAL] (state) {
    state.items = [];
    state.offset = 0;
    state.limit = 20;
    state.loading = false;
    state.pageCount = 0;
    state.pageNum = 0;
  },
  [SET_IS_LOADING_RELATED](state, loading) {
    state.loading = loading
  }
};

export default {
  state,
  actions,
  mutations,
  getters
};

function getDefaultState() {
  return {
    items: [],
    offset: 0,
    limit: 20,
    total: 0,
    loading: false,
    pageCount: 0,
    pageNum: 0,
    filteredTotal: 0
  }
}
